import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, of, tap } from 'rxjs';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { OTPResponse, VerifyOtpResponse, VerifyResetOtpResponse } from '../../models/interfaces/otp';
import { GetRolesResponse, UserAssingPayload, UserRoleDetailResponse } from '../../models/interfaces/userRole';
import { GoogleAuth, GoogleSignUpPayload, InvitationResponse, SignupPayload, SignupResponse } from '../../models/interfaces/signUp';
import { LoginPayload, LoginResponse, TokenExpirePayload, TokenExpireResponse } from '../../models/interfaces/login';
import { NewPasswordApiResponse, NewPasswordPayload } from '../../models/interfaces/newPassword';
import { ForgotPassword, ForgotPasswordResponse } from '../../models/interfaces/forgotPassword';
import { LogoutResponse } from '../../models/interfaces/logout';
import { environment } from '../../../../environments/environment';
import { UserDetailResponse } from '@edtech-ui/core/models/Classroom/user-detail';
import { InvitationTeacherResponse, TeacherOnBoardPayload } from '@edtech-ui/core/models/interfaces/invite-teacher-on-board';
import { EditUserProfilePayload, EditUserProfileResponse } from '@edtech-ui/core/models/user-profile/edit-user-profile';
import { SendOtpRequest, SendOtpResponse, VerifyOtpRequest } from '@edtech-ui/core/models/interfaces/mobileOtp';
import { jwtDecode } from 'jwt-decode';
import { SIDENAV_CONFIG, SidenavItem } from '../../constants/module-config.constants';
import { newSignUpPaylod, newSingupResoponse } from '@edtech-ui/core/models/interfaces/new-singup-flow/new-otp-mobile';
import { FireBaseServiceService } from '../firebaseService/fire-base-service.service';
import { OneSignalService } from '../one-signal-service/one-signal.service';
import { InactivityService } from '../../../auth/inactivity.service';
@Injectable({
	providedIn: 'root',
})
export class AuthServiceService {
	apiUrl = environment.baseUrl;

	private readonly USER_ID_KEY = 'user_id';
	private readonly ROLE_ID_KEY = 'role_id';
	private readonly EMAIL_KEY = 'email';
	private readonly TOKEN_KEY = 'token';
	private readonly TENANT_ID_KEY = 'tenant_id';
	private readonly FCM_TOKEN_KEY = 'fcmToken';

	private decodedToken: any = null;
	private permissionSubject = new BehaviorSubject<string[]>([]);
	permissions$ = this.permissionSubject.asObservable();
	private readonly PHONE_KEY = 'phone_num';

	currentUserEmail = new BehaviorSubject<string>('');
	currentUserId = new BehaviorSubject<string>('');
	resetToken = new BehaviorSubject<string>('');
	isTenantOnBoard = new BehaviorSubject<boolean>(true);
	isTenantOnBoardForGoogleAuth = new BehaviorSubject<boolean>(true);
	userStore$ = new BehaviorSubject<Partial<UserDetailResponse['Data']>>({});

	private allowedModulesSubject = new BehaviorSubject<string[]>([]);
	allowedModules$ = this.allowedModulesSubject.asObservable();
	private verifiedPhoneSubject = new BehaviorSubject<string | null>(null);
	verifiedPhone$ = this.verifiedPhoneSubject.asObservable();


	constructor(
		private readonly http: HttpClient,
		private readonly _firebaseService: FireBaseServiceService,
		private readonly _oneSignalService: OneSignalService,
		private readonly _inactivityService: InactivityService
	) {
		this.checkTokenPermissions();
		this.loadModules();
	}

	checkTokenPermissions() {
		const token = localStorage.getItem(this.TOKEN_KEY);
		if (token) {
			this.decodeTokenPermissions(token);
		}
	}

	decodeTokenPermissions(token: string): void {
		try {
			const decoded: any = jwtDecode(token);
			this.decodedToken = decoded;
			const decodePermissions = decoded.permissions || [];
			this.permissionSubject.next(decodePermissions);
			if (decoded.role_id) {
				localStorage.setItem(this.ROLE_ID_KEY, decoded.role_id.toString());
			}
			if (decoded.tenant_id) {
				localStorage.setItem(this.TENANT_ID_KEY, decoded.tenant_id);
			}
			if (decoded.user_id) {
				localStorage.setItem(this.USER_ID_KEY, decoded.user_id);
				this.currentUserId.next(decoded.user_id);
			}
			if (decoded.email) {
				localStorage.setItem(this.EMAIL_KEY, decoded.email);
				this.currentUserEmail.next(decoded.email);
			}
		} catch (error) {
			this.clearAll();
		}
	}

	hasPermission(permission: string): boolean {
		return this.permissionSubject.value.includes(permission);
	}

	hasAnyPermission(permissions: string[]): boolean {
		return permissions.some(permissions => this.hasPermission(permissions));
	}

	postNewSignUp(payload: newSignUpPaylod): Observable<newSingupResoponse> {
		return this.http.post<newSingupResoponse>(`${this.apiUrl}${environment.authEndpoints.newSignup}`, payload);
	}

	postSignUp(payload: SignupPayload): Observable<SignupResponse> {
		return this.http.post<SignupResponse>(`${this.apiUrl}${environment.authEndpoints.signup}`, payload);
	}

	postVerifyOTP(data: { email: string; otp: number }): Observable<VerifyOtpResponse> {
		return this.http.post<VerifyOtpResponse>(`${this.apiUrl}${environment.authEndpoints.verifyUserOtp}`, data);
	}

	postResendOtp(email: string): Observable<OTPResponse> {
		return this.http.post<OTPResponse>(`${this.apiUrl}${environment.authEndpoints.resendOtp}`, { email });
	}

	postUserRole(payload: UserAssingPayload): Observable<UserRoleDetailResponse> {
		return this.http.post<UserRoleDetailResponse>(`${this.apiUrl}${environment.authEndpoints.assignRoles}`, payload);
	}

	getUserRoles(): Observable<GetRolesResponse> {
		return this.http.get<GetRolesResponse>(`${this.apiUrl}${environment.authEndpoints.getRoles}`);
	}

	postLogin(data: LoginPayload): Observable<LoginResponse> {
		return this.http.post<LoginResponse>(`${this.apiUrl}${environment.authEndpoints.signin}`, data);
	}

	postForgotPassword(payload: ForgotPassword): Observable<ForgotPasswordResponse> {
		return this.http.post<ForgotPasswordResponse>(`${this.apiUrl}${environment.authEndpoints.forgotPassword}`, payload);
	}

	postVerifyResetOtp(data: { email: string; otp: number }): Observable<VerifyResetOtpResponse> {
		return this.http.post<VerifyResetOtpResponse>(`${this.apiUrl}${environment.authEndpoints.verifyResetOtp}`, data);
	}

	postSetNewPassword(payload: NewPasswordPayload): Observable<NewPasswordApiResponse> {
		return this.http.post<NewPasswordApiResponse>(`${this.apiUrl}${environment.authEndpoints.resetPassword}`, payload);
	}

	logout(router?: Router): Observable<LogoutResponse> {
		const user_id = this.getUserId();
		const token = this.getToken();
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token || ''}`,
		});
		return this.http.post<LogoutResponse>(`${this.apiUrl}${environment.authEndpoints.logout}`, { user_id }, { headers }).pipe(
			catchError((error) => {
				return of({ Success: true, Message: 'Logged out successfully' } as LogoutResponse);
			}),
			finalize(() => {
				if (router) {
					this.performLogoutCleanup(router);
				} else {
					this.clearAll();
				}
			})
		);
	}

	performLogoutCleanup(router: Router): void {
		const currentUserId = localStorage.getItem('user_id');
		if (currentUserId) {
			localStorage.setItem('last_active_user_id', currentUserId);
			localStorage.removeItem(`${currentUserId}_ai_quiz_data`);
			sessionStorage.removeItem(`popup_count_${currentUserId}`);
		} else {
			localStorage.removeItem('ai_quiz_data');
		}

		const keysToRemove = [
			'token', 'refresh_token', 'user_id', 'role_id', 'email', 'ai_quiz_data',
			'is_tenant_onboard', 'role_mode', 'companyId', 'companyStatus',
			'registeredEmail', 'company_id', 'modules', 'last_active_user_id',
			'user_time_zone', 'tenant_id', 'onesignal_player_id', 'modules', 'fcmToken', 'country', 'job_id', 'phone_email', 'fcmTokenTimestamp', 'company_id'
		];
		keysToRemove.forEach(key => localStorage.removeItem(key));

		localStorage.removeItem(this.TENANT_ID_KEY);
		sessionStorage.removeItem('free_trial_shown');
		// Clear both push notification tokens so fresh valid tokens are fetched on next login
		this._firebaseService.clearFCMToken();
		this._oneSignalService.clearOneSignalPlayerId();

		this.permissionSubject.next([]);
		this.decodedToken = null;
		this.allowedModulesSubject.next([]);

		router.navigate([this._inactivityService.getLoginRoute()]);
	}

	postGoogleSignUp(payload: GoogleSignUpPayload): Observable<GoogleAuth> {
		return this.http.post<GoogleAuth>(`${this.apiUrl}${environment.authEndpoints.googleAuth}`, payload);
	}

	getUserDetails(id: string): Observable<UserDetailResponse> {
		const headers = new HttpHeaders({
			'Cache-Control': 'no-cache, no-store, must-revalidate',
			'Pragma': 'no-cache',
			'Expires': '0'
		});
		return this.http.get<UserDetailResponse>(`${this.apiUrl}${environment.authEndpoints.userDetails}/${id}`, { headers }).pipe(
			tap((response) => {
				if (response && response.Success && response.Data) {
					this.userStore$.next(response.Data);
				}
			})
		);
	}

	postSentTeacherInvite(payload: TeacherOnBoardPayload): Observable<InvitationTeacherResponse> {
		return this.http.post<InvitationTeacherResponse>(`${this.apiUrl}${environment.authEndpoints.teacherInvite}`, payload);
	}

	refreshToken(payload: TokenExpirePayload): Observable<TokenExpireResponse> {
		return this.http.post<TokenExpireResponse>(`${this.apiUrl}${environment.authEndpoints.refreshToken}`, payload)
	}

	patchUserEditProfile(payload: EditUserProfilePayload | FormData): Observable<EditUserProfileResponse> {
		return this.http.patch<EditUserProfileResponse>(`${this.apiUrl}${environment.authEndpoints.userEditProfile}`, payload);
	}

	getInvitationById(invitationId: string) {
		const params = new HttpParams().set('invitation_id', invitationId);
		return this.http.get<InvitationResponse>(`${this.apiUrl}${environment.authEndpoints.invitation}`, { params });
	}

	sendPhoneOtp(payload: SendOtpRequest): Observable<SendOtpResponse> {
		return this.http.post<SendOtpResponse>(`${this.apiUrl}/send-phone-otp`, payload);
	}

	verifyPhoneOtp(payload: VerifyOtpRequest): Observable<VerifyOtpResponse> {
		return this.http.post<VerifyOtpResponse>(`${this.apiUrl}/verify-phone-otp`, payload);
	}

	resendPhoneOtp(payload: { phone_num: string, registration_source: 'web' }) {
		return this.http.post<any>(`${environment.baseUrl}/resend-phone-otp`, payload);
	}

	requestContactUpdateOtp(payload: { contact_type: string, new_contact: string }): Observable<any> {
		return this.http.post<any>(`${this.apiUrl}/profile/request-contact-otp`, payload);
	}

	verifyContactUpdateOtp(payload: { contact_type: string, new_contact: string, otp: number, token: string }): Observable<any> {
		return this.http.post<any>(`${this.apiUrl}/profile/verify-contact-update`, payload);
	}

	setUserId(userId: string): void {
		localStorage.setItem(this.USER_ID_KEY, userId);
		this.currentUserId.next(userId);
	}

	getUserId(): string | null {
		return localStorage.getItem(this.USER_ID_KEY);
	}

	clearUserId(): void {
		localStorage.removeItem(this.USER_ID_KEY);
		this.currentUserId.next('');
	}

	setRoleId(roleId: string): void {
		localStorage.setItem(this.ROLE_ID_KEY, roleId);
	}

	getRoleId(): string | null {
		return localStorage.getItem(this.ROLE_ID_KEY);
	}

	clearRoleId(): void {
		localStorage.removeItem(this.ROLE_ID_KEY);
	}

	setEmail(email: string): void {
		localStorage.setItem(this.EMAIL_KEY, email);
		this.currentUserEmail.next(email);
	}

	getEmail(): string | null {
		return localStorage.getItem(this.EMAIL_KEY);
	}

	getPhone(): string | null {
		return localStorage.getItem(this.PHONE_KEY);
	}

	clearEmail(): void {
		localStorage.removeItem(this.EMAIL_KEY);
		this.currentUserEmail.next('');
	}

	setToken(token: string): void {
		localStorage.setItem(this.TOKEN_KEY, token);
		this.decodeTokenPermissions(token);
	}

	getToken(): string | null {
		return localStorage.getItem(this.TOKEN_KEY);
	}

	setFcmtoken(fcmToken: string): void {
		localStorage.setItem(this.FCM_TOKEN_KEY, fcmToken);
	}

	getFcmToken(): string | null {
		return localStorage.getItem(this.FCM_TOKEN_KEY);
	}


	clearToken(): void {
		localStorage.removeItem(this.TOKEN_KEY);
	}


	setVerifiedPhone(phone: string) {
		this.verifiedPhoneSubject.next(phone);
	}

	getVerifiedPhone(): string | null {
		return this.verifiedPhoneSubject.value;
	}

	clearVerifiedPhone() {
		this.verifiedPhoneSubject.next(null);
	}

	setModules(modules: string[]) {
		this.allowedModulesSubject.next(modules);
		localStorage.setItem('modules', JSON.stringify(modules));
	}


	getModules(): string[] {
		const modules = localStorage.getItem('modules');
		if (modules) {
			return JSON.parse(modules);
		}
		return [];
	}


	loadModules() {
		const modules = this.getModules();
		if (modules.length > 0) {
			this.allowedModulesSubject.next(modules);
		}
	}


	isAuthorized(item: SidenavItem): boolean {
		if (item.permission) {
			const permissions = Array.isArray(item.permission) ? item.permission : [item.permission];
			if (permissions.length > 0 && !this.hasAnyPermission(permissions)) {
				return false;
			}
		}
		if (item.excludePermission) {
			const excludePermissions = Array.isArray(item.excludePermission) ? item.excludePermission : [item.excludePermission];
			if (this.hasAnyPermission(excludePermissions)) {
				return false;
			}
		}
		return true;
	}


	getFirstAllowedRoute(): string {
		const modules = this.allowedModulesSubject.value;

		for (const item of SIDENAV_CONFIG) {
			if (item.subItems && item.subItems.length > 0) {
				for (const sub of item.subItems) {
					if (modules.includes(sub.module) && this.isAuthorized(sub)) {
						return sub.route;
					}
				}
			}

			if (modules.includes(item.module) && this.isAuthorized(item)) {
				if (item.route) {
					return item.route;
				}
			}
		}
		return '/access-denied';
	}

	clearAll(): void {
		this.clearUserId();
		this.clearRoleId();
		this.clearEmail();
		this.clearToken();
		localStorage.removeItem(this.TENANT_ID_KEY);
		this.permissionSubject.next([]);
		this.decodedToken = null;
		this.allowedModulesSubject.next([]);
		localStorage.removeItem('modules');
		sessionStorage.removeItem('free_trial_shown');
	}

}